import { Currency, DateFormat, DecimalFormat, ThousandSeparator, TimeFormat, TimeZone } from '@shared/localisation'
import type { ScheduleTypeDay } from '@shared/schedule'
import type { NotificationSettings } from '@shared/notification'
import type { ReportPeriodConfig } from '@services/reportService'
import { Task } from '@components/task/types'
import { TemplateResult } from 'lit'
import { GettingStartedAnswers, PersonGettingStarted } from '@src/models/getting-started'
import { ObjectId } from 'mongodb'
import { BillingCurrency, SubscriptionInterval, SubscriptionProduct } from '@shared/subscription'

export type Id = string

export enum Action {
	archive = 'archive',
	bulkArchive = 'bulkArchive',
	bulkInvite = 'bulkInvite',
	bulkRemove = 'bulkRemove',
	bulkUnarchive = 'bulkUnarchive',
	duplicate = 'duplicate',
	copy = 'copy',
	cut = 'cut',
	paste = 'paste',
	remove = 'remove',
	rename = 'rename',
	unarchive = 'unarchive',

	timesheetEditRow = 'timesheetEditRow',
	timesheetRemoveRow = 'timesheetRemoveRow',
	timesheetGoToEntity = 'timesheetGoToEntity',
	timesheetPinRow = 'timesheetPinRow',
	goToTimesheet = 'goToTimesheet',
	goToTasks = 'goToTasks',
	timesheetUnpinRow = 'timesheetUnpinRow',

	scheduleClearRow = 'scheduleClearRow',
	scheduleTypeAddTimePair = 'scheduleTypeAddTimePair',
	scheduleCopyToNext = 'scheduleCopyToNext',

	moveLeft = 'moveLeft',
	moveRight = 'moveRight',

	reportChangeOutput = 'reportChangeOutput',
	reportExport = 'reportExport',
	reportFilter = 'reportFilter',
	reportMove = 'reportMove',
	reportPeriod = 'reportPeriod',
	reportResetPeriod = 'reportResetPeriod',
	reportReceiveByEmail = 'reportReceiveByEmail',
	reportSchedule = 'reportSchedule',
	reportShare = 'reportShare',
}

export type TokenResponse = {
	currentSession: {
		csrftoken: string
		authenticated: boolean
	}
}

// A GraphQL error from the server carries the path, locations and calling query alongside its
// message. A failure raised on the client, such as a lost connection, only has the message.
export type GqlError = {
	message: string
	path?: string[]
	locations?: { line: number; column: number }[]
	callingMsg?: string
}

export type Variables = Record<string, unknown>

/** A picture crop: x, y, width, height in source pixels */
export type Crop = [number, number, number, number]

export type GqlOperation = {
	query: string
	variables?: Variables
	isCreation?: boolean
	local?: () => void
	/** Refetch once the server answered a replay: for a resolver that skips this session's notification */
	reload?: () => Promise<void>
}

export enum CustomPageElements {
	person = 'person-page',
	project = 'project-page',
	task = 'task-page',
	settings = 'settings-page',
	role = 'role-page',
	absenceType = 'absence-type-page',
	expenseType = 'expense-type-page',
	scheduleType = 'schedule-type-page',
	tag = 'tag-page',
	planning = 'planning-page',
	timesheet = 'timesheet-page',
	customField = 'custom-field-page',
	journal = 'journal-page',
	reports = 'reports-page',
	settingsAccountDelete = 'settings-account-delete',
	integrations = 'integrations-page',
	settingsAccountSubscription = 'settings-account-subscription',
	settingsReleaseNotes = 'settings-release-notes',
	masterData = 'master-data-page',
	ai = 'ai-page',
	oauthConsent = 'oauth-consent',
	gettingStarted = 'getting-started-page',
}

export type EntityPicture = {
	id?: string
	url: string
	crop?: [number, number, number, number] // [x, y, width, height] in pixels from original image
}

export type Colors = {
	default: {
		both: string
		text: string
		bg: string
	}
	reverted: {
		both: string
		text: string
		bg: string
	}
}

export type Entity = {
	id: BeeboleId
	name: string
	color: number | null
	picture?: EntityPicture
	archived?: boolean
	lastEdited?: LastEdited
}

export type EntityWithType = Entity & { type: EntityType }

export type BeeboleId = string

export type BeeboleStatus = {
	id: BeeboleId
	name: string
	color: number
	maxConcurrentTasks: number
	taskIds: BeeboleId[]
	category: Category
}

/** What a planning (task category) holds: a to-do list, or people booked on projects. */
export type PlanningMode = 'tasks' | 'bookings'

export type Category = {
	id: BeeboleId
	name: string
	color?: number | null
	lastEdited?: LastEdited
	levelNames?: string[]
	namedLevelsOnly?: boolean
	statuses?: BeeboleStatus[]
	childrenIds?: BeeboleId[]
	taskMove?: number
	/** Task categories only — project and tag categories have no mode. */
	mode?: PlanningMode
}

export type CategoryEntity = Entity & {
	category: Category
	// `parent.referenced` marks a link synthesised by withReferencedAncestors (the parent is an
	// injected out-of-scope row, not one the backend resolved) — cleared on every re-derivation.
	parent: { id: BeeboleId; referenced?: boolean }
	// Parent id, readable even when the parent itself is out of scope (`parent` resolves the whole
	// entity, so auth strips it then). Anchors the ancestor chain the backend ships in the response
	// extensions — see referencedAncestorContext. (#1796)
	parentId?: BeeboleId
	// An out-of-scope link injected by withReferencedAncestors to complete a hierarchy: shown as a
	// read-only row so its descendants sit at the right depth, never selectable or counted. (#1796)
	referenced?: boolean
	ancestors: (CategoryEntity & { levelNames?: string[] })[]
	children: CategoryEntity[]
	level: number
	entityType?: EntityType
}

export type EntityListItem = (CategoryEntity | Entity) & {
	category?: {
		id: BeeboleId
	}
	isExpanded?: boolean
	isFiltered?: boolean
	children?: EntityListItem[]
	level?: number
	parent?: {
		id: BeeboleId
	}
	startTime?: Time | null
	endTime?: Time | null
	dependencies?: CategoryEntity[]
	childrenIds?: BeeboleId[]
	idx?: number //row number
	priority?: number //priority to sort
	ancestors?: (CategoryEntity & { levelNames?: string[] })[]
	isLeaf?: boolean
	referenced?: boolean
	operation?: RelationOperation
}

export enum EntityType {
	organisation = 'Organisation',
	person = 'Person',
	role = 'Role',
	absenceType = 'AbsenceType',
	scheduleType = 'ScheduleType',
	status = 'Status',
	tag = 'Tag',
	project = 'Project',
	task = 'Task',
	timesheet = 'Timesheet',
	customField = 'CustomField',
	expenseType = 'ExpenseType',
	journal = 'Journal',
}

/** Filter-only types that map to specific relation-based filters (not entities themselves) */
export enum FilterType {
	owner = 'owner',
	ownerTag = 'ownerTag',
	assignedPerson = 'assignedPerson',
	assignedTag = 'assignedTag',
	status = 'status',
	approvalStatus = 'approvalStatus',
	personTag = 'personTag',
	personCustomField = 'personCustomField',
	projectTag = 'projectTag',
	taskTag = 'taskTag',
	projectCategory = 'projectCategory',
	taskCategory = 'taskCategory',
	message = 'message',
	wfh = 'wfh',
	isAbsence = 'isAbsence',
	nonBillable = 'nonBillable',
	billable = 'billable',
}

// Union type
export type CustomFieldValueEntityType = EntityType | 'TimeRecord'

export type EntityBadge = (CategoryEntity | Entity) & {
	type: EntityType
	path?: AttributePath[]
	ancestors?: CategoryEntity[]
	operation?: RelationOperation
}

export type Maybe<T> = NonNullable<T> | undefined
export type Nullable<T> = T | null

export type AttributePath = {
	collection: string
	id: BeeboleId
	name: string
}

export type ValuePath<T> = {
	relationId?: string
	path: AttributePath[]
	value: T
	startTime?: BeeboleDate
	operation?: RelationOperation
}

export type ValueArrayPath<T> = {
	path: AttributePath[]
	value: T[]
	startTime?: number
}

export type TaskOwner = {
	occupation: number
	person: Partial<Person>
}

export type AvailableForPath = ValuePath<AvailableFor>
export type PersonPath = ValuePath<Person>
export type TagPath = ValuePath<Tag>
export type ProjectPath = ValuePath<Project>
export type TaskOwnerPath = ValuePath<Person>
export type TaskProjectPath = ValuePath<Project>

export type Members = {
	availableFor: AvailableFor
	persons: PersonPath[]
	tags: TagPath[]
}

export enum AvailableFor {
	everyone = 'everyone',
	nobody = 'nobody',
	members = 'members',
}

export type Localisation = {
	currency: { value: Currency }
	dateFormat: { value: DateFormat }
	decimalFormat: { value: DecimalFormat }
	thousandSeparator: { value: ThousandSeparator }
	timeFormat: { value: TimeFormat }
	timeZone: { value: TimeZone }
	firstDayOfWeek: { value: number }
}

export type TimeSettings = {
	projectCategories: ValueArrayPath<Project>
	listOfProjectCategories: ValueArrayPath<Category[]>
	taskCategories: ValueArrayPath<Category>
	periodicity: ValuePath<Periodicity>
	allowTimeOffRecord: ValuePath<boolean>
	allowTimeRecordOutOfScheduleDaily: ValuePath<boolean>
	allowTimeRecordOutOfSchedulePeriod: ValuePath<boolean>
	requireScheduledTimeDaily: ValuePath<boolean>
	requireScheduledTimePeriod: ValuePath<boolean>
	allowPartialPeriodSubmit: ValuePath<boolean>
	turnOffPeriodAfter: ValuePath<number>
	minimumTimeEntry: ValuePath<number>
	durationFormat: ValuePath<number>
	timeEntryUnit: ValuePath<number>
	noFutureTimeRecord: ValuePath<boolean>
	timeRecordOnWorkingDayOnly: ValuePath<boolean>
	timeRecordWithinScheduleIntervals: ValuePath<'warn' | 'force' | null>
	absenceMaxOneDay: ValuePath<boolean>
	lockDate: ValuePath<{ ts: number }>
	firstDayOfWeek: ValuePath<number>
	ownerOrAdminOnly: ValuePath<boolean>
	plannedTasksOnly: ValuePath<boolean>
	employeeAdminOnly: ValuePath<boolean>
	forceComment: ValuePath<boolean>
	hideNonBillable: ValuePath<boolean>
	enableTimer: ValuePath<boolean>
	enableStartEndTime: ValuePath<boolean>
	mandatoryTimer: ValuePath<boolean>
	mandatoryStartEndTime: ValuePath<boolean>
	timesheetReminder: ValuePath<TimesheetReminder>
	approvalReminder: ValuePath<number>
	unassignedTaskReminder: ValuePath<number>
	enableAutoTimesheet: ValuePath<boolean>
	autoTimesheet: ValueArrayPath<AutoTimesheetEntry>
}

export type AutoTimesheetEntry = {
	categoryId: BeeboleId
	category?: Category
	timesheetStartStatusId?: BeeboleId | null
	timesheetStartStatus?: BeeboleStatus
	timesheetEndStatusId?: BeeboleId | null
	timesheetEndStatus?: BeeboleStatus
}

export type Integrations = {
	asana: {
		enabled: boolean
		defaultRole: ObjectId
		accountId: string
		importMode: string
		user: string
		updating: boolean
	}
	monday: {
		enabled: boolean
		defaultRole: ObjectId
		accountId: string
		importMode: string
		user: string
		updating: boolean
	}
	jira: {
		enabled: boolean
		defaultRole: ObjectId
		accountId: string
		importMode: string
		user: string
		updating: boolean
	}
	quickbooks: {
		enabled: boolean
		defaultRole: ObjectId
		accountId: string
		user: string
		updating: boolean
		exportedFrom: number
		exportedTo: number
	}
	linear: {
		enabled: boolean
		defaultRole: ObjectId
		accountId: string
		importMode: string
		user: string
		updating: boolean
	}
	bamboo: {
		enabled: boolean
		defaultRole: ObjectId
		companyDomain: string
		updating: boolean
		importedFrom: number
		importedTo: number
	}
	xero: {
		enabled: boolean
		tenantId: string
		tenantName: string
		user: string
		updating: boolean
		clientCategoryId: string
	}
	webhooks: WebhookSubscription[]
}

import type { SSOProviderConfig, GoogleSSOConfig, SSOSettings, CustomSSOConfig } from '@shared/sso'
export type { SSOProviderConfig, GoogleSSOConfig, SSOSettings, CustomSSOConfig }

export type ScheduledDeletion = {
	// The person who scheduled the deletion is stripped from the response for anyone who cannot
	// see that person, so the banner may have a date and no name.
	by?: { id: string; name: string }
	at: number
}

export type Organisation = Entity & {
	timeSettings: TimeSettings
	absenceQuotas: AbsenceQuotaPath[]
	billings: ValueArrayPath<Rate>
	schedules: ValuePath<ScheduleAssignment[]>
	costs: ValueArrayPath<Rate>
	publicHolidays: PublicHolidaysPath
	availability: Availability
	relations: Relations
	gettingStarted: GettingStartedAnswers
	integrations: Integrations
	subscription: Subscription
	localisation: Localisation
	sso?: SSOSettings
	customSSO?: CustomSSOConfig
	scheduledDeletion?: ScheduledDeletion
	locked?: boolean
}

export type Subscription = {
	status: string
	startDate: BeeboleDate
	endDate: BeeboleDate
	billingCurrency: BillingCurrency
	cancelAt: BeeboleDate
	portalURL?: string
	plan?: SubscriptionProduct
	interval?: SubscriptionInterval
	quantity?: number
	customerId?: string
	subscriptionId?: string
	total?: number
	activeCount: number
	activeAddons?: SubscriptionProduct[]
}

export type Availability = {
	absenceTypes: boolean
	expenseTypes: boolean
	scheduleTypes: boolean
	tasks: boolean
	customFields: boolean
	projects: boolean
	subProjects: boolean
}

export type PeopleManagerOf = {
	persons: Person[]
	tags: Tag[]
}

export type ManagerOf = {
	projects: ValuePath<Project>[]
	tags: ValuePath<Tag>[]
	persons: ValuePath<Person>[]
	tasks: ValuePath<Task>[]
	projectCategories: ValuePath<Category>[]
	taskCategories: ValuePath<Category>[]
	tagCategories: ValuePath<Category>[]
}

export type Person = Entity & {
	email: string
	role: Role
	hasJoined: boolean
	hasBeenInvited: boolean
	localisation: Localisation
	lang?: Lang
	managerOf: ManagerOf
	scheduleTypes: ValuePath<ScheduleAssignment[]>
	screenSettings: ScreenSettings
	tookOverBy: Person
	absenceQuotas: AbsenceQuotaPath[]
	timeSettings: TimeSettings
	billings: ValueArrayPath<Rate>
	costs: ValueArrayPath<Rate>
	publicHolidays: PublicHolidaysPath
	absenceTypes: ValuePath<AbsenceType>[]
	expenses: ValuePath<ExpenseType>[]
	tasks: ValuePath<Task>[]
	projects: ValuePath<Project>[]
	relations: Relations
	journalSettings: JournalSettings
	subProjectAvailability?: { value: boolean; path?: AttributePath[] }
	description?: string
	hasPasskey?: boolean
	notificationSettings?: ValuePath<NotificationSettings>
	gettingStarted?: PersonGettingStarted
}

export enum Theme {
	lite = 'lite',
	dark = 'dark',
	system = 'system',
}

export type TimesheetRow = {
	rowId: number
	projectIds?: BeeboleId[]
	categoryId?: BeeboleId
	taskId?: BeeboleId | null
	absenceId?: BeeboleId | null
	pinned?: boolean
}

export type DismissedActivity = {
	key: string
	ts: number
}

export type StaffingAllocationMode = 'pct' | 'perDay' | 'total'

/** Measures the Planned vs. Real burn-up can be read in. `margin` is derived from billing − cost. */
export type PlannedVsRealUnit = 'hours' | 'days' | 'billing' | 'cost' | 'margin'

export type SidePaneTab = 'approval' | 'team' | 'calendar' | 'suggestions'

/** A calendar the person can connect to their timesheet. */
export type CalendarProvider = 'google' | 'microsoft'

export type ScreenSettings = {
	detailsPanelRatio?: number
	detailsPanelRatioPerEntityType?: {
		entityType: EntityType
		ratio: number
	}
	attributeSettings?: Attribute[]
	sideBarOpen?: boolean
	theme?: Theme

	/** @deprecated Use activeViewType from task service instead */
	kanbanView?: boolean

	timesheetRows?: TimesheetRow[]
	timesheetDismissedActivities?: DismissedActivity[]

	activeProjectCategoryId?: string
	activeTagCategoryId?: string
	activeTaskCategoryId?: string
	activeReportFolderId?: string

	color?: number

	approvalPaneTab?: SidePaneTab | null
	approvalPaneWidth?: number
	approvalEntityWidth?: number
	showAllTeam?: boolean
	showAllApprovals?: boolean

	lastRoute?: string

	detailsPanelPinned?: boolean

	timesheetSuggestionsCollapsed?: boolean
	// The calendar view hides the connected calendar's events on the calendar button
	timesheetCalendarEventsHidden?: boolean
	timesheetCalendarView?: boolean
	timesheetCalendarDayZoom?: boolean

	plannedVsRealRemaining?: boolean
	plannedVsRealPlans?: string[]
	plannedVsRealUnit?: PlannedVsRealUnit
	/** Which entities the report lists: 'people', or '<projectCategoryId>:<level>' (-1 = all levels). */
	plannedVsRealAxis?: string

	/** Unit the last budget was stated in — the default the next new budget starts from. */
	budgetQuantityUnit?: BudgetQuantityUnit
	/** Budget status measures stacked in one column instead of one column each. */
	budgetStatusStacked?: boolean

	staffingAllocationMode?: StaffingAllocationMode

	/** Start date and duration the last booking was added with — the defaults the next one
	 *  starts from. The start is only reused while it is today or later. */
	bookingStartTime?: number
	bookingDurationDays?: number

	/** Mode the person last picked for a planning — the mode the next planning they create
	 *  starts in. Bookings until they pick one. */
	planningMode?: PlanningMode

	/** Period each selector outside the report folders remembers on its own, so a
	 *  choice survives navigation without one screen dictating another's window. */
	staffingCapacityPeriod?: ReportPeriodConfig
	absenceQuotaPeriod?: ReportPeriodConfig
	utilizationPeriod?: ReportPeriodConfig
	timesheetCompliancePeriod?: ReportPeriodConfig
}

export type Target =
	| 'me'
	| 'managedPeople'
	| 'managedProjects'
	| 'managedTasks'
	| 'ownedTasks'
	| 'managedProjectTasks'
	| 'managedPeopleTasks'
	| 'managedTags'
	| 'managedProjectMembers'
	| 'peopleColleagues'
	| 'projectColleagues'
	| 'taskColleagues'
	| 'assignedProjects'
	| 'assignedTasks'
	| 'organisation'

export type Authorisation = {
	name: string
	targets?: Target[]
	view: Target[] | null
	manage: Target[] | null
}

export type Role = Entity & {
	authorisations: Authorisation[]
}

export type AssignmentPeriod = {
	startTime: BeeboleDate
	endTime: BeeboleDate
}

export enum Unit {
	Hour = 'hour',
	Day = 'day',
}

export enum Interval {
	Month = 'month',
	Year = 'year',
}

export type ApprovalStatus = 'd' | 's' | 'a' | 'r' | null

export type ApprovalApprover = {
	person: Person
	ts: number
}

export type ApprovalStageDetail = {
	stage: number
	type: string
	approvedBy: ApprovalApprover[]
	pending: Person[]
	skipped?: boolean
}

export type ApprovalState = {
	hasWorkflow: boolean
	status: ApprovalStatus
	currentStage: number | null
	currentStageType: string | null
	pendingApprovers: Person[]
	canApprove?: boolean
	canSubmit?: boolean
	submitId: BeeboleId | null
	by: Person | null
	totalDuration?: number
	stageDetails?: ApprovalStageDetail[]
}

export type ApprovalEventItem = {
	id: BeeboleId
	submitId?: BeeboleId
	person: Person
	startTime: Time
	endTime: Time
	status: 'd' | 's' | 'a' | 'r'
	ts: Time
	by: Person
	stage: number
	comment?: string
	rejectedStage?: number
	systemAction?: string
	originalApprover?: Person
	newApprover?: Person
}

export type PendingApprovalItem = {
	person: Person
	startTime: Time
	endTime: Time
	status: ApprovalStatus
	currentStage: number | null
	pendingApprovers: Person[]
	submitId: BeeboleId | null
	totalDuration: number
	stageDetails?: ApprovalStageDetail[]
}

export type TeamMemberSource = {
	type: 'direct' | 'tag' | 'structure' | 'project' | 'timeRecords'
	name?: string
	color?: number
}

export type FullTeamMember = {
	person: Person
	sources: TeamMemberSource[]
	// The auto-submit deadline failed on this period: the timesheet stays a
	// draft and needs the approver's attention.
	autoSubmitFailed?: boolean
}

export type ExcessOccurrence = {
	frequency: number
	interval: Interval
}

export type AbsenceNotification = {
	goingNegative: boolean
	bookingInAdvanceAccrual: boolean
	excessOccurrences: ExcessOccurrence
}

//biWeekly, hourly, monthly, semiMonthly, weekly
export enum Periodicity {
	hourly = 'hourly',
	daily = 'daily',
	weekly = 'weekly',
	biWeekly = 'biWeekly',
	semiMonthly = 'semiMonthly',
	monthly = 'monthly',
}

export enum TimesheetReminderTiming {
	nextStart = 'nextStart',
	currentEnd = 'currentEnd',
}

export type TimesheetReminder = {
	timing: TimesheetReminderTiming
	hour: number
}

export enum DurationFormat {
	hhmm = 1,
	d = 2,
	hsm = 3, // hour-string-minute
}

export enum TimeEntryUnit {
	h = 1,
	d = 2,
	p = 3, // percentage
}

export enum PeriodDay {
	First = 'firstDay',
	Last = 'lastDay',
}

export enum Month {
	Jan = 0,
	Feb,
	Mar,
	Apr,
	May,
	Jun,
	Jul,
	Aug,
	Sep,
	Oct,
	Nov,
	Dec,
}

export enum Day {
	Sun = 0,
	Mon,
	Tue,
	Wed,
	Thu,
	Fri,
	Sat,
}

export enum Week {
	Sunday = 0,
	Monday = 1,
	Tuesday = 2,
	Wednesday = 3,
	Thursday = 4,
	Friday = 5,
	Saturday = 6,
}

export enum Awarded {
	first = 'firstDay',
	last = 'lastDay',
	next = 'firstDayNextPeriod',
}

export type Accrual = {
	isEnabled: boolean
	periodicity: Periodicity
	awarded: Awarded
	quantity: number
	allowNegativeBooking: boolean
	periodStartDay: number
}

export type Period = {
	day: PeriodDay
	month: Month
}

export enum RateMethod {
	fixed = 'fixed',
	daily = 'daily',
	hourly = 'hourly',
	nonBillable = 'nonBillable',
	noCost = 'noCost',
}

export enum RateSplitType {
	persons = 'persons',
	projects = 'projects',
	noSplit = 'noSplit',
}

export type Amount = {
	value: number
	currency: Currency
}

export type RateSplit = {
	name: string
	amount: Amount
	nonBillable: boolean
}

export type RatePersonSplit = { person: Person } & RateSplit
export type RateTagSplit = { tag: Tag } & RateSplit
export type RateProjectSplit = { project: Project } & RateSplit

export type Rate = {
	id: BeeboleId

	type: RateType
	startTime: BeeboleDate
	method: RateMethod
	splitType?: RateSplitType
	amount?: Amount
	personSplit?: RatePersonSplit[]
	tagSplit?: RateTagSplit[]
	projectSplit?: RateProjectSplit[]
	repeatEnabled: boolean
	repeat?: Repeat
}

export type RateType = 'billing' | 'cost'
export type CapRateType = 'Billing' | 'Cost'

export type AbsenceQuotaPath = ValuePath<AbsenceQuota>

export type AbsenceQuota = {
	id: BeeboleId

	absenceType: { id: BeeboleId }
	startTime: BeeboleDate
	endTime: BeeboleDate
	available: number
	endDateExtension: BeeboleDate
	carryForwardLimit: number
	accruedCorrection: number
	allowNegativeBalance: boolean
	recurrent: boolean
}

export type AbsenceType = Entity & {
	involveCosts: boolean
	unit: Unit
	accrual: Accrual
	absenceQuotas: AbsenceQuotaPath[]
	notification: AbsenceNotification
	relations: {
		absenceTypesOfPerson: ValuePath<Person>[]
		absenceTypesOfTag: ValuePath<Tag>[]
	}
}

export type ExpenseType = Entity & {
	isCurrency: boolean
	markup: number
	isInBudget: boolean
	relations: {
		customFields: ValuePath<CustomField>[]

		expenseTypesOfPerson: ValuePath<Person>[]
		expenseTypesOfTag: ValuePath<Tag>[]
		expenseTypesOfProject: ValuePath<Project>[]
	}
}

export type WebhookSubscription = {
	id: BeeboleId
	name: string
	url: string
	secret: string
	events: string[]
	active: boolean
	lastEdited?: LastEdited
}

export type ExpenseRecord = {
	id: BeeboleId

	expenseType: { id: BeeboleId; name: string; isCurrency: boolean }
	amount: Amount
	date: BeeboleDate
	project?: Project
	person?: Person
	comment: string
}

export enum BudgetSplitType {
	persons = 'persons',
	projects = 'projects',
	noSplit = 'noSplit',
}

export type BudgetSplit = {
	billingAmount?: Amount
	costAmount?: Amount
	quantity: number
}

export type BudgetPersonSplit = { person: Person } & BudgetSplit
export type BudgetProjectSplit = { project: Project } & BudgetSplit

/** A budget states its quantity in scheduled hours or scheduled days; a day is one person's
 *  scheduled day, so neither unit converts into the other. */
export type BudgetQuantityUnit = 'hours' | 'days'

export type Budget = {
	id: BeeboleId
	startTime: BeeboleDate | null
	comment: string
	billingAmount?: Amount
	costAmount?: Amount
	quantity: number
	quantityUnit: BudgetQuantityUnit
	splitType?: BudgetSplitType
	personSplit?: BudgetPersonSplit[]
	projectSplit?: BudgetProjectSplit[]
	thresholdNotifiedAt?: number | null
	overBudgetNotifiedAt?: number | null
}

// Budget Status
export type BudgetViewMode = 'HOURS' | 'BILLING' | 'COSTS'
export type BudgetSortBy = 'PERCENT_CONSUMED' | 'NAME' | 'REMAINING'
export type BudgetStatusFilter = 'ALL' | 'AT_RISK' | 'OVER_BUDGET'

export type BudgetStatusSplit = {
	entity: Entity
	splitType: string
	timeUnit: BudgetQuantityUnit
	hours: BudgetModeValues
	days: BudgetModeValues
	billing: BudgetModeValues
	costs: BudgetModeValues
	matchesFilter?: boolean
}

export type BudgetModeValues = {
	budget: number
	actual: number
	forecast: number
	percentConsumed: number
	remaining: number
}

export type BudgetStatusItem = {
	budgetId: BeeboleId
	project: Project
	parentProject?: Project
	isAggregated: boolean
	budget: number
	actual: number
	forecast: number
	percentConsumed: number
	remaining: number
	timeUnit: BudgetQuantityUnit
	hours: BudgetModeValues
	days: BudgetModeValues
	billing: BudgetModeValues
	costs: BudgetModeValues
	splits?: BudgetStatusSplit[]
	children?: BudgetStatusItem[]
	matchesFilter?: boolean
}

export type Schedule = Entity & {
	calendarStartTime: BeeboleDate
	length: number
	days: ScheduleDay[]
	relations: {
		scheduleTypesOfPerson: ValuePath<Person>[]
		scheduleTypesOfTag: ValuePath<Tag>[]
		scheduleTypesOfOrganisation: ValuePath<Organisation>[]
		scheduleTimelineOfPerson: ValuePath<Person>[]
		scheduleTimelineOfTag: ValuePath<Tag>[]
		scheduleTimelineOfOrganisation: ValuePath<Organisation>[]
		customFields: ValuePath<CustomField>
	}
}

export type ScheduleAssignment = {
	id: BeeboleId
	scheduleType: Schedule
	startTime: BeeboleDate
}

export type ScheduleDay = ScheduleTypeDay & {
	ts?: number
	date?: string
	id?: string
	/** Public holiday name — the day was folded to 0h capacity when set */
	holiday?: string
}

export type Tag = CategoryEntity & {
	levelNames: string[]
	localisation: Localisation
	scheduleTypes: ScheduleAssignment[]
	children?: Tag[]
	absenceQuotas: AbsenceQuotaPath[]
	timeSettings: TimeSettings
	projectCategories: ProjectPath[]
	billings: ValueArrayPath<Rate>
	costs: ValueArrayPath<Rate>
	publicHolidays: PublicHolidaysPath
	relations: Relations & {
		tagged?: {
			persons: ValuePath<Person>[]
			projects: ValuePath<Project>[]
			tasks: ValuePath<Task>[]
		}
	}
	tasks: ValuePath<Task>[]
	projects: ValuePath<Project>[]
	projectFilters: ValuePath<Project>[]
	subProjectAvailability?: { value: boolean; path?: AttributePath[] }
	description?: string
}

export type RelationOperation = 'sub' | 'add'

export type Relations = {
	absenceTypes?: ValuePath<AbsenceType>[]
	expenseTypes?: ValuePath<ExpenseType>[]
	subProjects?: ValuePath<Project>[]
	scheduleTypes?: ValuePath<Schedule>[]
	scheduleTimeline?: ValuePath<Schedule>[]
	projects?: ValuePath<Project>[]
	persons?: Person[]
	tags?: Tag[]
	tasks?: ValuePath<Task>[]
	projectCategories?: ValuePath<Category>[]
	subProjectCategories?: ValuePath<Category>[]
	taskCategories?: ValuePath<Category>[]
	taggedBy?: ValuePath<Tag>[]
	tagProjectFilters?: Tag[]
	customFields?: ValuePath<CustomField>[]
	managedBy?: ValuePath<Person>[]
	managerOf?: ManagerOf
}

export type Project = CategoryEntity & {
	availability?: { value: boolean; path?: AttributePath[] }
	subProjectAvailability?: { value: boolean; path?: AttributePath[] }
	levelNames: string[]
	localisation: Localisation
	members: Members
	timeSettings: TimeSettings
	billings: ValueArrayPath<Rate>
	costs: ValueArrayPath<Rate>
	expenseTypes: ValuePath<ExpenseType>[]
	projectFilters: ValuePath<Project>[]
	relations: {
		subProjects: ValuePath<Project>[]
		expenseTypes: ValuePath<ExpenseType>[]
		customFields: ValuePath<CustomField>[]
		managedBy: ValuePath<Person>[]
		taggedBy?: ValuePath<Tag>[]

		projectsOfPerson: ValuePath<Person>[]
		projectsOfTag: ValuePath<Tag>[]
		relatedProjectsOfTask: ValuePath<Task>[]
		subProjectsOfProject: ValuePath<Project>[]
		subProjectsOfTag: ValuePath<Tag>
	}
	description?: string
}

export type localisationStringValue = {
	value: string
}

export type EditedBy = {
	id: BeeboleId
	name: string
}

export type BeeboleDate = {
	ts: number
	iso: string
}

export type LastEdited = {
	by: EditedBy
	at: BeeboleDate
}

export enum Cropping {
	auto = 'fo-center',
	face = 'fo-face',
}

export enum AttributeName {
	AbsenceAccrual = 'absence-type-accrual',
	AbsenceNotification = 'absence-type-notification',
	AbsenceQuota = 'absence-type-quota',
	AbsenceUnit = 'absence-type-unit',
	ApprovalStages = 'approval-stages',
	Authorisations = 'authorisations',
	RelatedTo = 'related-to',
	Availability = 'availability',
	Billing = 'billing',
	Budget = 'budget',
	Cost = 'cost',
	Description = 'description',
	CustomFieldDetails = 'custom-field-details',
	CustomFieldVisibility = 'custom-field-visibility',
	CustomFieldValues = 'custom-field-values',
	ExpenseTypeDetails = 'expense-type-details',
	ExpenseRecords = 'expense-records',
	Localisation = 'localisation',
	Notification = 'notification',
	EmailTemplates = 'email-templates',
	Manager = 'manager',
	SSO = 'sso',
	PublicHolidays = 'public-holidays',
	Schedule = 'scheduleType',
	ScheduleRelations = 'schedule-type-relations',
	Tagged = 'tagged',
	TaskOwner = 'task-owner',
	TaskDateStatus = 'task-date-status',
	TaskProject = 'task-project',
	ProjectTasks = 'project-tasks',
	TaskPerson = 'task-person',
	Tags = 'tags',
	TimeSettings = 'time-settings',
	User = 'user',
	ManagedBy = 'managed-by',
	ValidityPeriod = 'validity-period',
	Preview = 'preview',
	Options = 'options',
}

export type Attribute = {
	name: AttributeName
	open?: boolean
	order?: number
	expanded?: boolean
	defaultSettings?: boolean
	entityTypes?: EntityType[]
	entityType?: EntityType
	gql?: string | ((entityType: EntityType) => string)
	// The field the module shows: missing from the loaded entity, it was withheld by the server and the module is hidden
	field?: string
	preview?: boolean
}

export type ImageKitResponse = {
	fileId: string
	name: string
	size: number
	versionInfo: { id: string; name: string }
	filePath: string
	fileType: string
	height: number
	width: number
	url: string
	AITags: null
}

export type GcsUploadResponse = {
	uploadUrl: string
	publicUrl: string
	filePath: string
}

export type IBreadCrumb = { [key in 'name' | 'id' | 'entityType']: string }

export enum Languages {
	en = 'English',
	es = 'Español',
	fr = 'Français',
	de = 'Deutsch',
	it = 'Italiano',
	pt = 'Português',
	nl = 'Nederlands',
	pl = 'Polski',
	cs = 'Čeština',
	hu = 'Magyar',
}

export type Lang = keyof typeof Languages

export enum Select {
	none = 'none',
	some = 'some',
	all = 'all',
}

export type FetchOptions = {
	archived?: boolean
	callback?: () => void
}

export type JSONValue = string | number | boolean | JSONObject | Array<JSONValue>
export type JSONObject = {
	[k: string]: JSONValue
}

export type Service = Record<string, any> & {
	entityType: EntityType
	items?: any[]
	selectedId?: string
}

/*export type Service = {
	items: any[]
	entityType: EntityType
	selectedItemId: string
	selectedId: string
	filters?: any
	showFilters?: any
	hasFilters?: boolean
	loadData?: (forceReload?: boolean) => void
	fetchAll?: () => void
	fetchAllData?: () => void
	fetchOne?: (id: string) => Promise<void>
	add?: (name: string, categoryId?: string, parentId?: string, cb?: Function, variables?: any) => void
	remove?: (id: string, cb?: Function, variables?: any) => Promise<void>
	// Add other common service methods as needed
}*/

export type BuildSelectOptions = {
	sortBy?: 'value' | 'key' | 'none'
	group?: string
}

export type Position = {
	x: number
	y: number
}

export type Time = {
	ts: number
	iso?: string
}

export enum MonthRepeatPattern {
	firstDay = 'firstDay',
	lastDay = 'lastDay',
	date = 'date',
	weekday = 'weekday',
}

export enum Occurrence {
	day = 'day',
	week = 'week',
	month = 'month',
	year = 'year',
}

export type Repeat = {
	occurrence: Occurrence
	frequency: number
	weekPattern: Week[] | null
	monthPattern: MonthRepeatPattern | null
	endTimeEnabled: boolean
	endTime: Time
}

// ===================== Task Recurrence ========================

export type RecurrenceMode = 'calendar' | 'afterCompletion'

export enum RecurrenceOccurrence {
	day = 'day',
	workday = 'workday',
	week = 'week',
	month = 'month',
	year = 'year',
}

export type MonthPattern =
	| { type: 'dayOfMonth'; day: number }
	| { type: 'dayOfWeek'; week: number; dayOfWeek: number }
	| { type: 'firstDay' }
	| { type: 'lastDay' }

export type HolidayBehavior = 'skip' | 'shiftForward' | 'shiftBackward'

export type RecurrenceRule = {
	mode: RecurrenceMode
	occurrence: RecurrenceOccurrence
	frequency: number
	weekPattern?: number[]
	monthPattern?: MonthPattern
	holidayBehavior?: HolidayBehavior
	endTime?: number
	count?: number
}

// A custom field value as a time record carries it and as addTimeRecord takes it back
export type TimeRecordCustomFieldValue = {
	customFieldId: BeeboleId
	textValue?: string
	numberValue?: number
	dateValue?: number
	dateTimeValue?: number
	urlValue?: string
	booleanValue?: boolean
}

export interface ITimeRecord {
	id: BeeboleId

	duration: number
	startTime?: Time
	endTime?: Time
	person?: Person
	projects?: Project[]
	task?: Task
	absence?: AbsenceType
	comment?: string
	nonBillable?: boolean
	wfh?: boolean
	customFieldValues?: TimeRecordCustomFieldValue[]
}

// What a record carries besides its span and activity, recreated with it
export type TimeRecordContent = Pick<ITimeRecord, 'comment' | 'nonBillable' | 'wfh' | 'customFieldValues'>

export interface ITimeRecordSuggestion {
	id: BeeboleId
	startTime?: Time
	endTime?: Time
	projects?: Project[]
	task?: Task
	duration: number
	source: string
	// Every source that contributed to a resolved winner's amount (ladder
	// order) — the carrier first, then the absorbed same-work sources.
	mergedSources?: string[]
	signalKey?: string
	confidence: number
	status: string
	// Hand-tuned: the engine never resizes an edited draft again. Undoing an
	// edit restores this flag along with the values.
	edited?: boolean
	// The note the accepted entry carries — a calendar event's title
	comment?: string
}

export type ApprovalDigestItemKind =
	| 'nonWorkingDay'
	| 'overtime'
	| 'afterHours'
	| 'archivedProject'
	| 'archivedTask'
	| 'nearLockDate'
	| 'overPlannedTask'
	| 'baselineDeviation'

export interface IApprovalDigestItem {
	kind: ApprovalDigestItemKind
	day?: Time
	project?: Project
	task?: Task
	duration?: number
	expected?: number
}

export interface IApprovalDigest {
	totalDuration: number
	baselineDuration?: number
	items: IApprovalDigestItem[]
}

export interface LitepickerDate {
	dateInstance: Date
	lang: string
}

export type PublicHolidaysPath = ValuePath<PublicHolidays>

export type PublicHolidays = {
	code: string
	lang: string
	holidays: Holiday[]
}

export type Holiday = {
	id?: string
	name: string
	date: BeeboleDate
	disabled?: boolean
}

export interface Message {
	id: BeeboleId
	date: BeeboleDate
	author?: Entity
	type: MessageType
	content: string
	mentions: EntityReference[]
	reference?: EntityReference
	isPinned: boolean
	replyTo?: Message
	auditGroup?: string
	level?: 'warning' | 'error'
	submitId?: string
	// Approval messages deep-link to the timesheet at this day via renderTimesheetLink. (#1163)
	timesheetDate?: string
}

export enum MessageType {
	message = 'message',
	timesheetApproved = 'timesheetApproved',
	auditTrail = 'auditTrail',
	notification = 'notification',
}

export interface EntityReference {
	id: BeeboleId
	type: EntityType
}

export type DialogModalOptions = {
	content?: TemplateResult
	isSticky?: boolean
	hideCloseButton?: boolean
	/** Size the dialog to its content instead of the standard width, for content narrower than it. */
	fitContent?: boolean
}

export interface JournalSettings {
	journalTimestamp?: number
	personTimestamp?: number
	projectTimestamp?: number
	taskTimestamp?: number
	/** The content kinds the person took out of the journal feeds (#1436) */
	hiddenContent?: JournalContentKind[]
}

/** What a journal feed shows, as the person sees it: a kind per button of the content bar */
export type JournalContentKind = 'logs' | 'approvals' | 'messages'

//enum customField
export enum CustomFieldType {
	text = 'text',
	date = 'date',
	datetime = 'datetime',
	number = 'number',
	url = 'url',
	boolean = 'boolean',
	uniqueId = 'uniqueId',
}

export interface TextFieldOptions {
	maxLength: number
	minLength: number
	validationRegex: string | null
	placeholder: string
	allowedValues: string[]
	useAllowedValues: boolean
}

export interface DateFieldOptions {
	minDate: number | null
	maxDate: number | null
}

export interface DatetimeFieldOptions {
	minDatetime: number | null
	maxDatetime: number | null
}

export interface NumberFieldOptions {
	min: number | null
	max: number | null
	step: number | null
	decimal: boolean
	decimalPlaces: number
	prefix: string
	suffix: string
}

export interface UrlFieldOptions {
	placeholder: string
}

export interface BooleanFieldOptions {
	trueLabel?: string | null
	falseLabel?: string | null
}

export interface UniqueIdFieldOptions {
	placeholder: string
	validationRegex: string | null
}

export type CustomField = Entity & {
	fieldType: CustomFieldType
	visibility: CustomFieldVisibility
	textOptions?: TextFieldOptions
	dateOptions?: DateFieldOptions
	datetimeOptions?: DatetimeFieldOptions
	numberOptions?: NumberFieldOptions
	urlOptions?: UrlFieldOptions
	booleanOptions?: BooleanFieldOptions
	uniqueIdOptions?: UniqueIdFieldOptions
	relations: {
		customFieldsOfPerson: ValuePath<Person>[]
		customFieldsOfProject: ValuePath<Project>[]
		customFieldsOfTag: ValuePath<Tag>[]
		customFieldsOfTask: ValuePath<Task>[]
		customFieldsOfAbsenceType: ValuePath<AbsenceType>[]
	}
}

export interface CustomFieldVisibility {
	persons: boolean
	absenceTypes: boolean
	projects: CustomFieldProjectVisibility[]
	tasks: CustomFieldTaskVisibility[]
	timeRecords: CustomFieldTimeRecordVisibility | null
}

export interface CustomFieldProjectVisibility {
	categoryId: string
	levels: number[]
}

export interface CustomFieldTaskVisibility {
	categoryId: string
	levels: number[]
}

export interface CustomFieldTimeRecordVisibility {
	projectCategories: string[] | null
	taskCategories: string[] | null
	absences: boolean
}

export type CustomFieldValue = {
	id: BeeboleId
	customField: CustomField
	entityType: CustomFieldValueEntityType
	entityId?: BeeboleId
	person?: Person
	project?: Project
	task?: Task
	tag?: Tag
	textValue?: string
	numberValue?: number
	dateValue?: number
	dateTimeValue?: number
	urlValue?: string
	booleanValue?: boolean
}

export enum AttributeType {
	absenceQuotas = 'absenceQuotas',
	billings = 'billings',
	budgets = 'budgets',
	costs = 'costs',
}

export type SelectorOptions = {
	categories?: Category[] | null
	selectedIds?: BeeboleId[]
	// Categories picked as a whole: neither they nor anything inside them is offered again.
	selectedCategoryIds?: BeeboleId[]
	leafOnly?: boolean
	categoryId?: BeeboleId
}

export type AlertType = 'danger' | 'success' | 'warning'

export type AlertMessage = {
	type: AlertType
	title: string
	keys?: string[]
	texts?: string[]
}
